/** @jsx jsx */
import i18n from 'i18n';
import { jsx } from '@emotion/core';
import { FormikValues } from 'formik';
import { keyBy, mapValues } from 'lodash';
import {
  boolean,
  mixed,
  object,
  string,
  lazy,
  number,
  array,
  ObjectSchema,
} from 'yup';

import step1 from 'assets/icons/declaration-form-step1.svg';
import step2 from 'assets/icons/declaration-form-step2.svg';
import step3 from 'assets/icons/declaration-form-step3.svg';
import step4 from 'assets/icons/declaration-form-step4.svg';
import { STEPS } from './constants';
import {
  ChallengesType,
  IChallengeListProductItem,
  IFormInputGroup,
} from 'interfaces';
import { IFormProduct, IFormProducts } from './interfaces';
import { getFormatedDates } from 'core/utils/date.helpers';
import { DeclarationFormQuestionsStep } from './DeclarationFormQuestionsStep';
import { DeclarationFormProductsStep } from './DeclarationFormProductsStep';
import { DeclarationFormProofStep } from './DeclarationFormProofStep';
import { DeclarationFormFinalStep } from './DeclarationFormFinalStep';

export const getFormSteps = ({
  needProof,
  declarationForm,
}: ChallengesType) => {
  return [
    {
      component: DeclarationFormProductsStep,
      stepper: {
        name: i18n.t('challenges.declaration.steps.results'),
        icon: <img src={step1} alt="loudspeaker" />,
      },
    },
    ...(needProof
      ? [
          {
            component: DeclarationFormProofStep,
            stepper: {
              name: i18n.t('challenges.declaration.steps.proof'),
              icon: <img src={step2} alt="camera" />,
            },
          },
        ]
      : []),
    ...(declarationForm
      ? [
          {
            component: DeclarationFormQuestionsStep,
            stepper: {
              name: i18n.t('challenges.declaration.steps.questions'),
              icon: <img src={step3} alt="asking" />,
            },
          },
        ]
      : []),
    {
      component: DeclarationFormFinalStep,
      stepper: {
        name: i18n.t('challenges.declaration.steps.send'),
        icon: <img src={step4} alt="sending" />,
      },
    },
  ];
};

export const makeInitialValues = (
  {
    challengeListProducts,
    needCurrencyValue,
    needProof,
    declarationForm,
  }: ChallengesType,
  oldFormiikValues: FormikValues | undefined,
): FormikValues => ({
  [STEPS.products]: mapValues(
    keyBy(challengeListProducts, 'challengeProduct.@id'),
    ({ challengeProduct }: IChallengeListProductItem): IFormProduct => {
      const oldValue =
        oldFormiikValues?.[STEPS.products]?.[challengeProduct['@id']];
      return {
        id: challengeProduct['@id'],
        ...(oldValue ?? {
          isEditing: false,
          isSelected: false,
          units: '',
          selectedUnits: 0,
          ...(needCurrencyValue && { value: '', selectedValue: 0 }),
        }),
      };
    },
  ),
  ...(needProof && { [STEPS.file]: undefined }),
  ...(declarationForm && {
    [STEPS.answers]: declarationForm.formInputGroups?.map(
      ({ formInputs }: IFormInputGroup) => ({
        id: formInputs[0]['@id'],
        type: formInputs[0].type,
        answer: '',
      }),
    ),
  }),
});

// 100Mo upload file size restriction
const PROOF_MAX_SIZE = 100000000;
const PROOF_ACCEPTED_FORMATS = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const makeValidationSchema = ({
  needCurrencyValue,
  needProof,
  declarationForm,
}: ChallengesType): ObjectSchema =>
  object().shape({
    [STEPS.products]: lazy((value: any) =>
      object().shape(
        mapValues(value, () =>
          object().shape({
            isEditing: boolean()
              .oneOf([false])
              .required(i18n.t('challenges.declaration.productCardError')),
            units: number().when(['isEditing'], {
              is: true,
              then: number().required(
                i18n.t('challenges.declaration.productCardError'),
              ),
            }),
            ...(needCurrencyValue && {
              value: number().when(['isEditing'], {
                is: true,
                then: number().required(
                  i18n.t('challenges.declaration.productCardError'),
                ),
              }),
            }),
          }),
        ),
      ),
    ),
    ...(needProof && {
      [STEPS.file]: mixed()
        .required()
        .test(
          'fileSize',
          i18n.t('challenges.declaration.fileSizeError'),
          (value?: File) => value !== undefined && value.size <= PROOF_MAX_SIZE,
        )
        .test(
          'fileType',
          i18n.t('challenges.declaration.fileTypeError'),
          (value?: File) =>
            value !== undefined && PROOF_ACCEPTED_FORMATS.includes(value.type),
        ),
    }),
    ...(declarationForm && {
      [STEPS.answers]: array().of(
        object().shape({
          answer: string().required(
            i18n.t('challenges.declaration.questions.error'),
          ),
        }),
      ),
    }),
  });

export const getDates = (challengeData: any, language: string) => {
  return getFormatedDates(
    {
      challenge: {
        start: challengeData?.startDate,
        end: challengeData?.endDate,
      },
      diffusion: {
        start: challengeData?.startDiffusionDate,
        end: challengeData?.endDiffusionDate,
      },
    },
    { day: 'numeric', month: 'short', year: 'numeric' },
    language,
  );
};

export const getSelectedProducts = (products?: IFormProducts): IFormProduct[] =>
  Object.values(products ?? {}).filter(
    ({ isSelected }: { isSelected: boolean }) => isSelected,
  ) || [];
